import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { apiClient } from "@/shared/api/client"

interface ProgressResponse {
	completed: number[]
}

const QUERY_KEY = ["beginner-path", "progress"] as const

export const useBeginnerProgress = () => {
	return useQuery<number[]>({
		queryKey: QUERY_KEY,
		queryFn: async () => {
			const response = await apiClient.get<ProgressResponse>("/beginner-path/progress")
			return response.data.completed
		},
		staleTime: 1000 * 60,
	})
}

export const useToggleBeginnerStep = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: async ({ step, completed }: { step: number; completed: boolean }) => {
			const path = `/beginner-path/progress/${step}`
			const response = completed
				? await apiClient.put<ProgressResponse>(path)
				: await apiClient.delete<ProgressResponse>(path)
			return response.data.completed
		},
		onMutate: async ({ step, completed }) => {
			await queryClient.cancelQueries({ queryKey: QUERY_KEY })
			const previous = queryClient.getQueryData<number[]>(QUERY_KEY) ?? []
			const next = completed
				? [...previous, step].sort((a, b) => a - b)
				: previous.filter((value) => value !== step)

			queryClient.setQueryData<number[]>(QUERY_KEY, next)
			return { previous }
		},
		onError: (_error, _variables, context) => {
			if (context) queryClient.setQueryData<number[]>(QUERY_KEY, context.previous)
		},
		onSuccess: (completed) => {
			queryClient.setQueryData<number[]>(QUERY_KEY, completed)
		},
	})
}
